import React, { useEffect, useMemo, useRef, useState } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import FormHelperText from "@mui/material/FormHelperText";

import CircularProgress from "@mui/material/CircularProgress";
import makeStyles from "@mui/styles/makeStyles";
import IconClose from "@mui/icons-material/Close";
import List from "@researchgate/react-intersection-list";
import { Input } from "../input";

import { IResponseMany, IQueryMany } from "../../interfaces";

const useStyles = makeStyles((theme) => ({
  selectPaper: {
    maxHeight: "40vh",
    overflowY: "auto",
    listStyle: "none",
  },
  chip: {
    marginRight: 4,
    marginBottom: 4,
    maxWidth: "100%",
  },
}));

interface IOption {
  value: string;
  [any: string]: any;
}

interface ISelectProps {
  className?: string; // custom className
  getOptionLabel?: (o: any) => string; // get label to be displayed in the options list
  getOptionSelected?: (o: any, v: any) => boolean; // function to determine if an option is selected from the value prop
  multiple?: boolean; // allow multiple selection
  allowAdd?: boolean; // allow adding to the list
  options?: IOption[];
  isLoading?: boolean;
  label?: any;
  placeholder?: any;
  returnKey?: string; // key from option object to return on change
  helperText?: string;
  size?: "small" | "medium"; // input density, forwarded to the rendered input

  // react-hook-form or custom control
  value?: any;
  onChange: (ev: any) => void;
  onBlur?: (ev: any) => void;
  inputRef?: any;
  error?: string;

  // async props
  fetcher?: (query: IQueryMany) => IResponseMany;
  query?: (search: string) => IQueryMany;

  optionToValue?: (option: string | { value: string }) => any;

  /** Optional extra filter applied to options (e.g. exclude already-selected) */
  filterOptions?: (options: any[], params: any) => any[];

  [any: string]: any;
}

interface IAsyncListBox extends React.HTMLAttributes<HTMLElement> {
  hasMore?: boolean; // flag to determine if new options should be loaded
  nextPage?: (payload: any) => any; // function to be called to fetch new options
  isLoading?: boolean; // flag to disable intersections while loading the first page
}

/**
 * Our application receives the options array in the following format
 * { name: string, value: string }[] - most of the times
 * { label: string, value: string }[] - some cases
 * string[] - rarely
 * note that we CANNOT return an object
 * */
const defaultGetOptionLabel = (option) => {
  if (!option) {
    return "";
  }
  if (typeof option === "object") {
    // Add "xxx" option created dynamically
    if (option.inputValue) {
      return option.inputValue;
    } else if (option.name) {
      return option.name;
    } else if (option.label) {
      return option.label;
    }
    // Value selected with enter, right from the input
  } else if (typeof option === "string") {
    return option;
  } else {
    return "";
  }
};

const defaultGetOptionSelected = (option, value) => {
  if (!value) {
    return false;
  }

  if (value.id) {
    return option.id === value.id;
  } else if (option.value) {
    if (value.value) {
      return option.value === value.value;
    } else {
      return option.value === value;
    }
  } else {
    return option === value;
  }
};

const filter = createFilterOptions();

const PAGE_SIZE = 30;

export const optionToObjectValue = (option) =>
  typeof option === "object"
    ? option
    : {
        value: option ?? null,
        label: option ?? "",
      };

export const optionToScalarValue = (option) =>
  (option?.hasOwnProperty("value") ? option.value : option) ?? null;

const useAsyncListBoxStyles = makeStyles((theme) => ({
  // Apply MUI autocomplete list container styles
  root: {
    margin: 0,
    padding: theme.spacing(1, 0),
    overflow: "auto",
    listStyle: "none",
    maxHeight: "40vh",
  },
}));

const AsyncListBoxComponent = React.memo(
  React.forwardRef((props: IAsyncListBox, ref) => {
    const {
      children,
      hasMore,
      nextPage,
      ...rest // material ui autocomplete options container props
    } = props;

    const classes = useAsyncListBoxStyles();
    // Autocomplete only ever renders option/group elements here
    const items = React.Children.toArray(children) as React.ReactElement[];

    return (
      <div
        {...rest}
        className={`${classes.root} ${
          "className" in rest ? rest.className : ""
        }`}
      >
        <List
          awaitMore={!!hasMore}
          pageSize={PAGE_SIZE}
          itemCount={items.length}
          onIntersection={nextPage}
          renderItem={(index) => items[index]}
        />
      </div>
    );
  })
);

const MuiSelect = React.forwardRef((props: ISelectProps, ref: any) => {
  const {
    // dedicated props
    multiple,
    allowAdd = false,
    options,

    // react-hook-form
    onChange,
    onBlur,
    value,

    inputRef,
    error,

    // extra
    className,
    placeholder,
    label,
    getOptionLabel = defaultGetOptionLabel,
    getOptionSelected = defaultGetOptionSelected,
    isLoading: isLoadingProp,
    returnKey,
    helperText,
    size = "medium",

    // async props
    fetcher,
    query,
    optionToValue = returnKey ? null : optionToObjectValue,
    filterOptions: filterOptionsProp,

    ...rest
  } = props;

  const classes = useStyles();
  const [searchValue, setSearchValue] = useState(""); // search value
  const isAsync = !!fetcher;
  const finalFetcher: (query: IQueryMany) => Partial<IResponseMany> =
    fetcher ||
    (() => ({
      data: null,
      awaitMore: null,
      nextPage: null,
    }));

  /**
   * In case there is a value already selected, display the initial options, instead of options matching the current value.
   * For this we need to perform an empty query search to our fetcher
   *
   * getOptionLabel - maps each option object to a given string
   * defaultGetOptionLabel = (option) => option?.name || option?.label || option;
   * */
  const searchTerm =
    isAsync && searchValue === getOptionLabel(value) ? "" : searchValue;
  const { data, awaitMore, nextPage, isValidating } = finalFetcher(
    isAsync ? query(searchTerm) : null
  );

  const optionList = useMemo(() => {
    if (isAsync) {
      return Array.isArray(data) ? data : [];
    }

    return options || [];
  }, [isAsync, data, options]);

  const isLoading = isAsync
    ? data === undefined ||
      data === null ||
      (!!isValidating && !optionList.length)
    : !!isLoadingProp;

  const resolvedOptionsCache = useRef<Record<string, any>>({});
  const multiValueCache = useRef<any[] | null>(null);

  useEffect(() => {
    resolvedOptionsCache.current = {};
    multiValueCache.current = null;
  }, [returnKey]);

  useEffect(() => {
    if (!returnKey) {
      return;
    }

    optionList?.forEach((option) => {
      if (option && typeof option === "object") {
        const optionKey = option[returnKey];

        if (optionKey !== undefined) {
          const cacheKey =
            typeof optionKey === "string"
              ? optionKey
              : optionKey === null
              ? "null"
              : optionKey === undefined
              ? "undefined"
              : JSON.stringify(optionKey);

          resolvedOptionsCache.current[cacheKey] = option;
        }
      }
    });

    // force multi-value normalization to re-run with the enriched cache
    multiValueCache.current = null;
  }, [optionList, returnKey]);

  const normalizedValue = useMemo(() => {
    const generateCacheKey = (value) => {
      return typeof value === "string"
        ? value
        : value === null
        ? "null"
        : value === undefined
        ? "undefined"
        : JSON.stringify(value);
    };

    const generateOptionCacheKey = (value) => {
      if (value && typeof value === "object") {
        return generateCacheKey(value[returnKey]);
      }
      return generateCacheKey(value);
    };

    const resolveOption = (value: any) => {
      const cacheKey = generateOptionCacheKey(value);

      if (typeof value === "object") {
        if (value && cacheKey !== "undefined") {
          resolvedOptionsCache.current[cacheKey] = value;
        }
        return value;
      }

      if (resolvedOptionsCache.current[cacheKey]) {
        return resolvedOptionsCache.current[cacheKey];
      }

      const matchedOption = optionList.find(
        (option) => option?.[returnKey] === value
      );

      if (matchedOption) {
        resolvedOptionsCache.current[cacheKey] = matchedOption;
        return matchedOption;
      }

      return value;
    };

    if (!returnKey) {
      return value;
    }

    if (multiple) {
      if (!Array.isArray(value) || value.length === 0) {
        multiValueCache.current = null;
        return value;
      }

      const resolvedArray = value.map(resolveOption);

      // Check resolvedArray same as multiValueCache
      if (
        multiValueCache.current &&
        multiValueCache.current.length === resolvedArray.length &&
        multiValueCache.current.every(
          (item, index) =>
            item === resolvedArray[index] ||
            JSON.stringify(item) === JSON.stringify(resolvedArray[index])
        )
      ) {
        return multiValueCache.current;
      }

      // Otherwise update multiValueCache
      multiValueCache.current = resolvedArray;
      return resolvedArray;
    }

    return resolveOption(value);
  }, [returnKey, value, optionList, multiple]);

  /**
   * Extra properties used for async options loading
   * */
  const autoCompleteProps = {
    loading: isLoading, // loading state indicator
    ...(isAsync && {
      inputValue: searchValue, // local state - keep 'search term' and load options based on current value
      onInputChange: (ev, newValue, reason) => {
        // Ignore resets in multi-select so the search text isn't cleared when chips re-render
        // Fixes a bug where user is unable to type in search input when options are selected
        if (reason === "reset" && multiple) {
          return;
        }

        setSearchValue(newValue);
      }, // update local state

      /**
       * Options container performing pagination request
       * */
      ListboxComponent: AsyncListBoxComponent,
    }),
    ...(isLoading && {
      clearIcon: <CircularProgress size={16} />,
    }),
    ListboxProps: {
      className: "marapp-qa-select-options-container",
      ...(isAsync && {
        hasMore: awaitMore,
        nextPage,
      }),
    },
  };

  return (
    <>
      <Autocomplete
        className={`marapp-qa-select ${className}`}
        clearIcon={
          <IconClose className="marapp-qa-select-clear" fontSize="small" />
        }
        ChipProps={{ className: `${classes.chip} marapp-qa-select-chip` }}
        disablePortal={true}
        fullWidth={true}
        disableCloseOnSelect={!!multiple}
        filterSelectedOptions={!!multiple}
        filterOptions={(availableOptions, params) => {
          // For async selects we trust server-side search; client filtering can hide valid results.
          let filtered = isAsync
            ? [...availableOptions]
            : filter(availableOptions, params);

          if (filterOptionsProp) {
            filtered = filterOptionsProp(filtered, params);
          }

          // if allowed, suggest the creation of a new value
          if (allowAdd && params.inputValue !== "") {
            if (!filtered.some((f) => f.value === params.inputValue)) {
              filtered.push({
                inputValue: params.inputValue,
                label: params.inputValue,
                value: params.inputValue,
              });
            }
          }

          return filtered;
        }}
        multiple={multiple}
        size={size}
        freeSolo={!!allowAdd}
        selectOnFocus={!!allowAdd}
        handleHomeEndKeys={!!allowAdd}
        options={optionList}
        value={normalizedValue}
        onBlur={onBlur}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={getOptionSelected}
        onChange={(_newValueIndex: any, newValue: IOption | IOption[]) => {
          if (isAsync) {
            // Clear the search box after a selection so the next query starts fresh
            setSearchValue("");
          }

          if (optionToValue) {
            if (Array.isArray(newValue)) {
              return onChange(newValue.map(optionToValue) ?? []);
            }

            return onChange(optionToValue(newValue));
          }

          const mapReturnValue = (option: any) => {
            if (!option || !returnKey) {
              return option;
            }

            if (typeof option === "object") {
              const mappedValue = option[returnKey];

              if (mappedValue !== undefined) {
                resolvedOptionsCache.current[String(mappedValue)] = option;
              }

              return mappedValue !== undefined ? mappedValue : option;
            }

            return option;
          };

          if (Array.isArray(newValue)) {
            // check each newValue, wrap if it is just a string
            if (returnKey) {
              multiValueCache.current = null;

              onChange(newValue?.map(mapReturnValue));
            } else {
              onChange(
                newValue?.map((v) =>
                  typeof v === "object" ? v : { value: v, label: v }
                )
              );
            }
          } else {
            onChange(returnKey ? mapReturnValue(newValue) : newValue);
          }
        }}
        ref={ref}
        renderInput={(params) => (
          <Input
            {...params}
            // Input takes a `size` of its own that it doesn't forward, so hand
            // the density Autocomplete resolved straight to the underlying
            // input — otherwise a small select still renders a full-height box.
            // Undefined at the default size, leaving those callers untouched.
            InputProps={{ ...params.InputProps, size: params.size }}
            label={label}
            placeholder={placeholder}
            error={error}
          />
        )}
        {...autoCompleteProps}
        {...rest}
      />

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </>
  );
});

export default React.memo(MuiSelect);
